import { useState, useEffect, useRef } from 'react';
import { 
    collection, 
    onSnapshot
} from 'firebase/firestore';
import type { 
    Unsubscribe,
    DocumentData,
    QuerySnapshot 
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import log from '../utils/logger';

export interface Platform {
    color: string;
    id: number;
}

export interface Dealer {
    dealerName: string;
    dealerPictureUrl: string;
    asaCompliant: boolean;
}

export interface TableConfig {
    tableName: string;
    launchAlias: string[];
    imsGameType: string;
    gameType: string;
    tableColor: string;
    dedicated: boolean;
    language: string;
    dynamicImageUrl: string;
    asaAlternativeImage?: string;
}

export interface Result {
    gameResultType: string;
    lastNumbers?: number[];
}

export interface Round {
    result: Result;
}

export interface Value {
    max: number;
    min: number;
}

export interface Limit {
    currency: string;
    values: Value[];
    vipLevel: number;
}

export interface GameLive {
    id: string;
    status: string;
    languageFlagImageUrl?: string;
    provider?: string;
    partnerID?: string;
    platform: Platform;
    platforms?: string[];
    playerCount?: number;
    freeSeatsCount?: number;
    dealer: Dealer;
    tableConfig: TableConfig;
    round?: Round;
    limits?: Limit[];
}

interface UseGamesLiveReturn {
    gamesLive: GameLive[];
    loading: boolean;
    error: string | null;
    isConnected: boolean;
}

export const useGamesLive = (isActive: boolean = true): UseGamesLiveReturn => {
    const [gamesLive, setGamesLive] = useState<GameLive[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const listenerSetup = useRef(false);
    const unsubscribeRef = useRef<Unsubscribe | null>(null);

    useEffect(() => {
        if (!isActive) {
            // Se il tab non è attivo, disconnetti il listener se esiste
            if (unsubscribeRef.current) {
                log.debug('🔌 Disconnessione listener games_live (tab non attivo)');
                unsubscribeRef.current();
                unsubscribeRef.current = null;
                listenerSetup.current = false;
                setIsConnected(false);
            }
            return;
        }

        const setupRealtimeListener = () => {
            // Evita setup multipli in StrictMode
            if (listenerSetup.current) return;
            listenerSetup.current = true;
            try {
                log.debug('🔥 Configurazione listener Firestore per games_live...');
                
                // Prima prova: query semplice senza ordinamento
                const q = collection(db, 'games_live');
                
                // Listener in tempo reale
                unsubscribeRef.current = onSnapshot(
                    q,
                    (snapshot: QuerySnapshot<DocumentData>) => {
                        // Log solo se è il primo caricamento o se ci sono errori
                        if (snapshot.metadata.fromCache || snapshot.metadata.hasPendingWrites) {
                            log.debug('📡 Snapshot da cache o con scritture pendenti');
                        }
                        
                        const games: GameLive[] = [];
                        
                        snapshot.forEach((doc) => {
                            const data = doc.data();
                            //log.debug(`📄 Documento ${doc.id}:`, data);
                            
                            games.push({
                                id: doc.id,
                                ...data
                            } as GameLive);
                        });

                        // Log solo se il numero di giochi cambia
                        setGamesLive(prevGames => {
                            if (prevGames.length !== games.length) {
                                log.debug(`📡 Games Live aggiornati: ${games.length} giochi (precedenti: ${prevGames.length})`);
                            }
                            return games;
                        });
                        
                        setLoading(false);
                        setError(null);
                        setIsConnected(true);
                    },
                    (err) => {
                        log.error('❌ Errore nel listener games_live:', err);
                        log.error('❌ Dettagli errore:', {
                            code: err.code,
                            message: err.message,
                            stack: err.stack
                        });
                        setError(`Errore nel caricamento: ${err.message}`);
                        setLoading(false);
                        setIsConnected(false);
                    }
                );
                
                log.debug('✅ Listener games_live configurato correttamente');
            } catch (err) {
                log.error('❌ Errore nella configurazione del listener:', err);
                setError('Errore nella configurazione del listener');
                setLoading(false);
                setIsConnected(false);
            }
        };

        setupRealtimeListener();

        // Cleanup function
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
                listenerSetup.current = false;
                if (process.env.NODE_ENV === 'development') {
                    log.debug('🔌 Listener games_live disconnesso');
                }
            }
        };
    }, [isActive]); // Re-run quando isActive cambia

    return {
        gamesLive,
        loading,
        error,
        isConnected
    };
};

// Hook utilitario per filtrare giochi live per categoria
export const useGamesLiveByCategory = (category?: string, isActive: boolean = true): UseGamesLiveReturn => {
    const { gamesLive, loading, error, isConnected } = useGamesLive(isActive);
    const [filteredGames, setFilteredGames] = useState<GameLive[]>([]);

    useEffect(() => {
        if (category) {
            const filtered = gamesLive.filter(game => 
                'category' in game && typeof game.category === 'string' && game.category.toLowerCase() === category.toLowerCase()
            );
            setFilteredGames(filtered);
        } else {
            setFilteredGames(gamesLive);
        }
    }, [gamesLive, category]);

    return {
        gamesLive: filteredGames,
        loading,
        error,
        isConnected
    };
};

// Hook utilitario per ottenere solo giochi attivi
export const useActiveGamesLive = (isActive: boolean = true): UseGamesLiveReturn => {
    const { gamesLive, loading, error, isConnected } = useGamesLive(isActive);
    const [activeGames, setActiveGames] = useState<GameLive[]>([]);

    useEffect(() => {
        const active = gamesLive.filter(game => 'isActive' in game && game.isActive === true);
        setActiveGames(active);
    }, [gamesLive]);

    return {
        gamesLive: activeGames,
        loading,
        error,
        isConnected
    };
};